/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react'
import Image from '../../imgix-img'
import ProfileInformation from '../form-helpers/profile-information'
import { useForm } from 'react-hook-form'
import { useComponentTracking } from '../../../utils/component-events'
import { maskPII } from '../../../utils/form'
import {
  Button,
  Field,
  FieldGroup,
  Icon,
  Input,
  Label,
  Radio,
  RadioGroup,
  Select,
} from '../../../atoms'
import { setConfig } from '../../helpers/consent'
import { borrowingDurationAmountOptions } from './data'
import DirectorFinder, { DirectorDetails } from '../form-helpers/director-details'
import uuid from '../../../utils/guid'
import { readCookieRaw } from '../../../utils/cookies'
import { fetchWithRetry } from '../../../utils/fetch-with-retry'
import ProgressCircle from '../../form/progress-circle'
import { connect } from 'react-redux'
import { getComponentById } from '../../../redux/component/component-selectors'
import { FsCore } from 'types/fs-core'
import { brand } from '../../../redux/app-selectors'
import ProductDetails from '../../product-details'
import HomeAddress from '../form-helpers/home-address'
import { NumberInput } from '../form-helpers/number-input'
import { fetchProfile, piiData, prefillFormData, ProfileFormData } from '../form-helpers/utils'
import Prefill from '../form-helpers/prefill'
import InEligible from '../form-helpers/ineligible'

interface IwocaFormProps {
  brand: FsCore.FSBrand
}

const { __FS_API_URL__ } = global
const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'
const formName = 'Iwoca form'

// eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
let debounce: NodeJS.Timeout

const IwocaForm: React.FC<IwocaFormProps> = ({ brand, productData, schema }) => {
  const [showBusinessDetailsSection, setShowBusinessDetailsSection] = React.useState(false)
  const [consents, setConsents] = React.useState({ marketing: true })
  const [businessDirectors, setBusinessDirectors] = React.useState<DirectorDetails[]>([])
  const [validationError, setValidationError] = React.useState<string | null>(null)
  const [submitLoading, setSubmitLoading] = React.useState(false)
  const [prefilled, setPrefilled] = React.useState({ prefilled: false, loading: false })
  const [lowTurnover, setLowTurnover] = React.useState(false)
  const [prefillProfile, setPrefillProfile] = React.useState<ProfileFormData | null>(null)

  const trackComponent: (
    eventName: string,
    subEventName: string,
    payload: Record<string, any>,
  ) => void = useComponentTracking()

  const trackFormFieldInteracted = (detail: string, value: any) => {
    return trackComponent('Form Event', 'formFieldInteracted', {
      formStep: detail,
      selected: piiData.includes(detail) ? 'untracked' : value,
    })
  }

  React.useEffect(() => {
    setConfig({
      apiPath: window.location.href.includes('staging')
        ? 'https://www.staging.money.co.uk/consent'
        : 'https://www.money.co.uk/consent',
      clientId: 'fs-core',
      brand: 'money',
    })
    setSubmitLoading(false)
    setValidationError(null)
  }, [])

  const {
    register,
    errors,
    setValue,
    watch,
    unregister,
    handleSubmit,
    control,
    setError,
    clearErrors,
  }: { register: (validation: any) => any } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      businessDetails: {
        businessType: 'limitedCompany',
      },
      directorDetails: {
        title: undefined,
        firstName: undefined,
        lastName: undefined,
        dateOfBirth: undefined,
        emailAddress: undefined,
        mobileNumber: undefined,
        nationality: undefined,
      },
      otherDetails: {
        fscsConsent: false,
        productConsent: false,
        marketingConsent: false,
        isSameHomeAddress: 'yes',
      },
    },
  })

  const companyNumber = watch('businessDetails.companyNumber')
  const directorFirstName = watch('directorDetails.firstName')
  const isSameHomeAddress = watch('otherDetails.isSameHomeAddress')
  React.useEffect(() => {
    const fetchDirectorData = async () => {
      const res = await fetch(`/api/company/${companyNumber}/officers`)
      const json = await res.json()
      const directors = json.data.filter((d) => d.officer_role === 'director')
      setBusinessDirectors(directors)
    }
    if (companyNumber) {
      fetchDirectorData()
    }
  }, [companyNumber])

  React.useEffect(() => {
    const smeSession = readCookieRaw('sme-session-id')
    if (smeSession) {
      setPrefilled({ loading: true, prefilled: false })
      fetchProfile()
        .then((profileData) => {
          setPrefillProfile(profileData)
          if (profileData.businessDetails.businessType === 'limitedCompany') {
            prefillFormData(profileData, setValue, register, trackFormFieldInteracted).then(() => {
              setPrefilled({ loading: false, prefilled: true })
              setShowBusinessDetailsSection(true)
              trackComponent('Form Event', 'prefillInfo', {
                FormName: formName,
                FieldData: maskPII(
                  { ...profileData.businessDetails, ...profileData.directorDetails },
                  piiData,
                ),
              })
            })
          } else {
            setPrefilled({ loading: false, prefilled: false })
          }
        })
        .catch((e) => {
          setPrefilled({ loading: false, prefilled: false })
          // eslint-disable-next-line no-console
          console.error(e.message)
        })
    }
  }, [])
  React.useEffect(() => {
    clearTimeout(debounce)
    debounce = setTimeout(() => {
      setLowTurnover(parseInt(watch('businessDetails.turnover')) < 25000)
    }, 1000)
  }, [watch('businessDetails.turnover')])

  const registerWithTrack = (validation: any, detail: any) => {
    return register({
      ...validation,
      validate: (value: any) => {
        trackFormFieldInteracted(detail, value)
        return true
      },
    })
  }

  const handleBusinessDetailsSubmit = async (finalBody: Object) => {
    const res = await fetch(
      `${apiPath}/sme-profile?brand=money&deviceId=${readCookieRaw('uscd')}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalBody),
      },
    )
    trackComponent('Form Event', 'sectionCompleted', {
      SectionName: 'Profile Information',
      FormName: formName,
      FieldData: maskPII(finalBody, ['businessAddress', 'email']),
      status: res.ok ? 'Success' : 'Failed',
    })
    setShowBusinessDetailsSection(true)
  }
  const inEligibleProfile = !prefillProfile
    ? false
    : prefillProfile?.businessDetails?.businessType !== 'limitedCompany'
  return (
    <div className='ProviderForm'>
      <div className='SMEform'>
        <div>
          <h3>Apply for an iwoca business loan</h3>
          <div className='SMEform__logo'>
            <Image
              src={
                'https://img.money.co.uk/s3/uswitch-assets-eu/amp/images/provider/logos/iwoca_2021.png'
              }
              height={66}
              width={180}
              critical={false}
              alt={'iwoca Logo'}
            />
          </div>
        </div>
        {(prefilled.loading || prefilled.prefilled) && <Prefill loading={prefilled.loading} />}
        {inEligibleProfile && <InEligible setPrefillProfile={setPrefillProfile} />}
        {!inEligibleProfile && (
          <form onSubmit={(e) => e.preventDefault()} autoComplete='off'>
            <ProfileInformation
              registerWithTrack={registerWithTrack}
              register={register}
              setValue={setValue}
              unregister={unregister}
              businessDetails={watch('businessDetails')}
              directorDetails={watch('directorDetails')}
              hideButton={showBusinessDetailsSection}
              consents={consents}
              setConsents={setConsents}
              setError={setError}
              clearErrors={clearErrors}
              errors={errors}
              brand={brand}
              consentSource={'iwoca-journey'}
              businessOptions={[{ name: 'limitedCompany', text: 'Limited Company' }]}
              companyFiltering={['ltd', 'plc', 'llp']}
              formName={formName}
              ctaText='Continue to business details'
              handleSubmit={handleBusinessDetailsSubmit}
              control={control}
              defaultBusinessNameInput={watch('businessNameInput')}
            />
            {showBusinessDetailsSection ? (
              <>
                <div className='SMEform__companyDetails__detailsBox'>
                  <div className='SMEform__title__box'>
                    <Label className='SMEform__title'>Loan details</Label>
                  </div>
                  <div className='SMEform__input'>
                    <Field>
                      <Label className={'required'}>
                        What is your turnover in the last 12 months (£)?
                      </Label>
                      <NumberInput
                        name='businessDetails.turnover'
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'Please enter the annual turnover of your business',
                          },
                        }}
                        trackField={(name: string, value: string) =>
                          trackComponent('Form Event', 'formFieldInteracted', {
                            formStep: name,
                            selected: value,
                          })
                        }
                        defaultValue={watch('businessDetails.turnover')}
                      />
                      {errors?.businessDetails?.turnover && (
                        <div className='SMEform__error'>
                          {errors.businessDetails.turnover.message}
                        </div>
                      )}
                      {lowTurnover && (
                        <div className='SMEform__infoBox'>
                          <Icon glyph='info-blob' size={24} />
                          <div>
                            To qualify for iwoca&apos;s startup business loan your business needs an
                            annual turnover of at least £25,000. Explore other products that may be
                            a better fit for now. <br />{' '}
                            <a href='/business/business-loans/alternate-finance-options'>
                              View other loan options
                            </a>
                          </div>
                        </div>
                      )}
                    </Field>
                    <Field>
                      <Label className={'required'}>How much are you looking to borrow (£)?</Label>
                      <NumberInput
                        name='businessDetails.borrowingAmount'
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'Please enter how much you would like to borrow',
                          },
                          max: {
                            value: 10000000,
                            message: 'Amount must be less than £10,000,000',
                          },
                          min: {
                            value: 1,
                            message: 'Amount must be more than £1',
                          },
                        }}
                        defaultValue={watch('businessDetails.borrowingAmount')}
                        trackField={(name: string, value: string) =>
                          trackComponent('Form Event', 'formFieldInteracted', {
                            formStep: name,
                            selected: value,
                          })
                        }
                      />
                      {errors?.businessDetails?.borrowingAmount && (
                        <div className='SMEform__error'>
                          {errors.businessDetails.borrowingAmount.message}
                        </div>
                      )}
                    </Field>
                    <Field>
                      <Label className={'required'}>How long do you need the funds for?</Label>
                      <FieldGroup stack={true}>
                        <Select
                          name='businessDetails.borrowingDurationAmount'
                          options={[
                            { text: 'Please Select', value: '' },
                            ...borrowingDurationAmountOptions,
                          ]}
                          onChange={(e) => {
                            setValue('businessDetails.borrowingDurationAmount', e.target.value)
                          }}
                          value={watch('businessDetails.borrowingDurationAmount') ?? ''}
                          ref={registerWithTrack(
                            {
                              required: 'Please tell us the duration you would like to borrow for',
                            },
                            'borrowingDurationAmount',
                          )}
                        />
                        <Select
                          name='businessDetails.borrowingDurationUnit'
                          options={[
                            { text: 'Please Select', value: '' },
                            { text: 'Days', value: 'days' },
                            { text: 'Months', value: 'months' },
                            { text: 'Years', value: 'years' },
                          ]}
                          onChange={(e) => {
                            setValue('businessDetails.borrowingDurationUnit', e.target.value)
                          }}
                          value={watch('businessDetails.borrowingDurationUnit') ?? ''}
                          ref={registerWithTrack(
                            {
                              required: {
                                value: true,
                                message: 'Please tell us the duration you would like to borrow for',
                              },
                            },
                            'borrowingDurationUnit',
                          )}
                        />
                      </FieldGroup>

                      {(errors?.businessDetails?.borrowingDurationUnit ||
                        errors?.businessDetails?.borrowingDurationAmount) && (
                        <div className='SMEform__error'>
                          Please tell us the duration you would like to borrow for
                        </div>
                      )}
                    </Field>
                    <Field>
                      <Label className={'required'}>What is the finance for?</Label>
                      <Select
                        name='businessDetails.borrowingReason'
                        value={watch('businessDetails.borrowingReason') ?? ''}
                        onChange={(e) =>
                          setValue('businessDetails.borrowingReason', e.target.value)
                        }
                        options={[
                          { text: 'Please Select', value: '' },
                          { text: 'Bridging Loan', value: 'bridging_loan' },
                          { text: 'Marketing', value: 'marketing' },
                          { text: 'Stock Purchase', value: 'stock_purchase' },
                          { text: 'Equipment Purchase', value: 'equipment_purchase' },
                          { text: 'Pay Bill', value: 'pay_bill' },
                          { text: 'Pay Staff', value: 'pay_staff' },
                          { text: 'Refinancing Debt', value: 'refinancing_debt' },
                          { text: 'Financing Client Order', value: 'financing_client_order' },
                          { text: 'Pay Tax Bill', value: 'pay_tax_bill' },
                          { text: 'Other', value: 'other' },
                        ]}
                        ref={registerWithTrack(
                          {
                            required: {
                              value: true,
                              message: 'Please select the reason for borrowing',
                            },
                          },
                          'borrowingReason',
                        )}
                      />
                      {errors?.businessDetails?.borrowingReason && (
                        <div className='SMEform__error'>
                          {errors.businessDetails.borrowingReason.message}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                {lowTurnover && (
                  <Button href='/business/business-loans/alternate-finance-options'>
                    Explore other funding options
                  </Button>
                )}
                {!lowTurnover && (
                  <DirectorFinder
                    directorDetails={watch('directorDetails', {})}
                    businessDirectors={businessDirectors}
                    registerWithTrack={registerWithTrack}
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                  />
                )}
                {directorFirstName && (
                  <Field className='SMEform__typeOfBusiness'>
                    <Label>Is your business address the same as your home address?</Label>
                    <div className='SMEform__input'>
                      <RadioGroup column={false}>
                        <Radio
                          name='otherDetails.isSameHomeAddress'
                          value='yes'
                          currentValue={watch('otherDetails.isSameHomeAddress')}
                          onChange={(e) => {
                            setValue(`otherDetails.isSameHomeAddress`, e.target.value)
                          }}
                          ref={registerWithTrack(
                            {
                              required:
                                'Please tell us if your home address is the same as your business address',
                            },
                            'isSameHomeAddress',
                          )}
                        >
                          Yes
                        </Radio>
                        <Radio
                          name='otherDetails.isSameHomeAddress'
                          value='no'
                          currentValue={watch('otherDetails.isSameHomeAddress')}
                          onChange={(e) => {
                            setValue(`otherDetails.isSameHomeAddress`, e.target.value)
                          }}
                          ref={registerWithTrack(
                            {
                              required:
                                'Please tell us if your home address is the same as your business address',
                            },
                            'isSameHomeAddress',
                          )}
                        >
                          No
                        </Radio>
                      </RadioGroup>
                      {errors?.businessDetails?.isSameHomeAddress && (
                        <div className='SMEform__error'>
                          {errors?.businessDetails?.isSameHomeAddress.message}
                        </div>
                      )}
                    </div>
                  </Field>
                )}
                {!lowTurnover && isSameHomeAddress === 'no' && (
                  <HomeAddress
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    addressFieldName={'directorDetails.address'}
                  />
                )}
                <div className='SMEform__productDetailsMobile'>
                  <ProductDetails productData={productData} schema={schema} />
                </div>
                {!lowTurnover && (
                  <Field className='SMEform__typeOfBusiness'>
                    <Label>Review Terms</Label>
                    <div className='SMEform__input'>
                      <label className='SMEform__fscsConsent'>
                        <Input
                          type='checkbox'
                          name='otherDetails.productConsent'
                          ref={registerWithTrack(
                            {
                              required: 'Please review and accept the product terms',
                            },
                            'productConsent',
                          )}
                        />
                        <div>
                          I have read and accepted the current{' '}
                          <a
                            href='https://static.iwoca.com/pdf/limited_liability.5e2ae8343090.pdf'
                            target='blank'
                          >
                            Terms and Conditions
                          </a>{' '}
                          and agree to apply for a iwoca business loan.{' '}
                          <a
                            href='https://www.iwoca.co.uk/compliance/customer-privacy-policy'
                            target='blank'
                          >
                            Privacy policy
                          </a>
                        </div>
                      </label>

                      <div className='SMEform__fscsConsent'>
                        <ul>
                          <li>
                            iwoca will use your personal information and that of your company
                            directors, beneficial owners or guarantors in the following ways.
                          </li>
                          <li>
                            Share information with fraud prevention agencies - if fraud is detected
                            you could be refused services, finance, or employment.
                          </li>
                          <li>
                            Record calls and correspondence between you and us for training and
                            reference purposes
                          </li>
                          <li>
                            Share information with credit reference agencies to run soft credit and
                            identity checks -{' '}
                            <strong>this won’t affect your personal credit score</strong>
                          </li>
                        </ul>
                      </div>

                      {(errors?.otherDetails?.productConsent ||
                        errors?.otherDetails?.fscsConsent) && (
                        <div className='SMEform__error'>Please tick all the checkboxes above</div>
                      )}
                      <div className='SMEform__fscsConsent'>
                        <p>
                          We will show you a range of products from our partners. View their{' '}
                          <a href='https://www.money.co.uk/privacy-notice/partners' target='blank'>
                            privacy policies
                          </a>{' '}
                          for more information.
                        </p>
                      </div>
                    </div>
                  </Field>
                )}
                {!lowTurnover && (
                  <div className='SMEform__buttonWrapper'>
                    {submitLoading ? (
                      <div className='SMEform__loadingMessage'>
                        <div className='SMEform__loadingMessage__spinner'>
                          <ProgressCircle height={48} />
                        </div>
                        <p>
                          Thanks for your patience, it should only take a minute or two.
                          iwoca&apos;s just checking everything you&apos;ve provided. Please
                          don&apos;t hit refresh or back
                        </p>
                      </div>
                    ) : (
                      <Button
                        onClick={handleSubmit(async (data) => {
                          const { businessDetails, directorDetails } = data
                          const finalBody = {
                            companyRegistrationNumber: businessDetails.companyNumber,
                            registeredBusinessAddress: businessDetails.address,
                            businessType: businessDetails.businessType,
                            nationality: directorDetails.nationality,
                            title: directorDetails.title,
                            firstName: directorDetails.firstName,
                            lastName: directorDetails.lastName,
                            dateOfBirthDay: directorDetails.dateOfBirthFields.day.replace(
                              /^(?:0+(?=[1-9])|0+(?=0$))/gm,
                              '',
                            ),
                            dateOfBirthMonth: directorDetails.dateOfBirthFields.month.replace(
                              /^(?:0+(?=[1-9])|0+(?=0$))/gm,
                              '',
                            ),
                            dateOfBirthYear: directorDetails.dateOfBirthFields.year,
                            emailAddress: directorDetails.emailAddress,
                            mobileNumber: directorDetails.mobileNumber,
                            businessName: businessDetails.businessName,
                            turnover: parseInt(businessDetails.turnover),
                            homeAddress: directorDetails.address?.postcode
                              ? directorDetails.address
                              : businessDetails.address,
                            sic_code: businessDetails.sic_code,
                            date_of_creation: businessDetails.date_of_creation,
                            dateOfCreation: businessDetails.dateOfCreation,
                            borrowingAmount: parseInt(businessDetails.borrowingAmount),
                            borrowingReason: businessDetails.borrowingReason,
                            borrowingDurationUnit: businessDetails.borrowingDurationUnit,
                            borrowingDurationAmount: businessDetails.borrowingDurationAmount,
                            companyType: businessDetails.companyType,
                          }
                          try {
                            setSubmitLoading(true)
                            setValidationError(null)
                            trackComponent('Form Event', 'sectionCompleted', {
                              SectionName: 'Application Form Information',
                              FormName: formName,
                              FieldData: maskPII(finalBody, [
                                'registeredBusinessAddress',
                                'emailAddress',
                                'homeAddress',
                                'firstName',
                                'lastName',
                                'dateOfBirthDay',
                                'dateOfBirthMonth',
                                'dateOfBirthYear',
                                'title',
                                'mobileNumber',
                                'workNumber',
                                'nationality',
                              ]),
                            })
                            trackComponent('Form Event', 'formSubmitted', {
                              formName: formName,
                            })
                            const res = await fetch(
                              `${apiPath}/iwoca/application?brand=money&requestId=${uuid()}&deviceId=${readCookieRaw(
                                'uscd',
                              )}`,
                              {
                                method: 'POST',
                                body: JSON.stringify(finalBody),
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                              },
                            )
                            if (res.ok) {
                              const res = await fetchWithRetry(
                                `${apiPath}/iwoca/application?brand=money&check=true&deviceId=${readCookieRaw(
                                  'uscd',
                                )}`,
                                120000,
                              )
                              if (res === undefined || !res.ok || res.status === 202) {
                                setValidationError(
                                  `Oops looks like something went wrong. Timeout Error`,
                                )
                                setSubmitLoading(false)
                              } else {
                                const response = await res.json()
                                if (
                                  response.status === 'decline' ||
                                  response.status === 'declined'
                                ) {
                                  window.location.href = '/business/iwoca/no-results'
                                } else if (response.status !== 'Error') {
                                  window.location.href = '/business/iwoca/results'
                                } else {
                                  setValidationError('Oops looks like something went wrong.')
                                  setSubmitLoading(false)
                                }
                              }
                            } else {
                              const result = await res.json()
                              if (result.errors && Array.isArray(result.errors)) {
                                setValidationError(
                                  `Oops looks like something went wrong. ${JSON.stringify(
                                    result.errors?.[0]?.message ?? result.errors,
                                  )}`,
                                )
                              } else {
                                setValidationError(
                                  `Oops looks like something went wrong. ${JSON.stringify(result)}`,
                                )
                              }
                              setSubmitLoading(false)
                            }
                          } catch (e) {
                            setValidationError(
                              `Oops looks like something went wrong. Please try again later. ${e}`,
                            )
                            setSubmitLoading(false)
                          }
                        })}
                        disabled={!directorFirstName}
                        variant='primary'
                        size='lg'
                        type='submit'
                      >
                        Submit
                      </Button>
                    )}
                  </div>
                )}
                {validationError && (
                  <div className='SMEform__validationError'>{validationError}</div>
                )}
              </>
            ) : (
              <>
                <div className='SMEform__companyDetails__detailsBox'>
                  <div className='SMEform__title'>Loan details</div>
                </div>
                <div className='SMEform__companyDetails__detailsBox'>
                  <div className='SMEform__title'>Director details</div>
                </div>
              </>
            )}
          </form>
        )}
      </div>
      <div className='SMEform__productDetailsDesktop'>
        <ProductDetails productData={productData} schema={schema} />
      </div>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    productData: getComponentById(state, props.sys.id).productData,
    ...props,
  }
})(IwocaForm)
